export class GameAudio {
  private context: AudioContext | null = null;
  private enabled: boolean;

  constructor(enabled: boolean) {
    this.enabled = enabled;
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  async unlock(): Promise<void> {
    if (!this.enabled) return;
    this.context ??= new AudioContext();
    if (this.context.state === "suspended") await this.context.resume();
  }

  grab(): void {
    this.tone(210, 0.045, "triangle", 0.035, 45);
  }

  launch(speed: number): void {
    this.tone(155 + Math.min(speed / 8, 180), 0.09, "sawtooth", 0.025, -70);
  }

  scream(speed: number, voice: number): void {
    if (speed < 520) return;
    const pitch = 390 + Math.min(speed / 4, 360) + voice * 19;
    this.tone(pitch, 0.13, "sawtooth", 0.012, 75, 0.025);
    this.tone(pitch * 1.08, 0.17, "triangle", 0.01, -110, 0.1);
  }

  rim(): void {
    this.tone(720, 0.045, "square", 0.025, -260);
  }

  gasp(): void {
    for (let index = 0; index < 4; index += 1) {
      this.tone(470 + index * 63, 0.12, "triangle", 0.011, -120, index * 0.012);
    }
  }

  score(perfect: boolean, streak: number): void {
    this.tone(perfect ? 660 : 520, 0.11, "sine", 0.05, 210);
    this.tone(perfect ? 990 : 780, 0.16, "sine", 0.04, 140, 0.075);
    const voices = Math.min(5, 2 + Math.floor(streak / 2));
    for (let index = 0; index < voices; index += 1) {
      this.tone(180 + index * 34, 0.2, "triangle", 0.009, 80, 0.03 + index * 0.018);
    }
  }

  countdown(final: boolean): void {
    this.tone(final ? 660 : 340, final ? 0.16 : 0.08, "square", 0.03, final ? 260 : 0);
  }

  private tone(
    frequency: number,
    duration: number,
    type: OscillatorType,
    volume: number,
    frequencyDelta: number,
    delay = 0,
  ): void {
    if (!this.enabled || !this.context) return;

    const now = this.context.currentTime + delay;
    const oscillator = this.context.createOscillator();
    const gain = this.context.createGain();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, now);
    oscillator.frequency.exponentialRampToValueAtTime(Math.max(40, frequency + frequencyDelta), now + duration);
    gain.gain.setValueAtTime(volume, now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    oscillator.connect(gain).connect(this.context.destination);
    oscillator.start(now);
    oscillator.stop(now + duration);
  }
}
